import { Text } from '@cretesuite/design-system'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { checkmarkCircleOutline } from 'ionicons/icons'
import { useEffect, useMemo, useState } from 'react'
import {
  type Control,
  type FieldValues,
  useForm,
  useWatch,
} from 'react-hook-form'
import { useMutation, useQuery } from 'urql'
import { TermsAcknowledgment } from '../components/TermsAcknowledgment'
import { Signature } from '../components/form/Signature'
import {
  CollectOrderSignature,
  GetCustomerContacts,
  GetOrderDataForSignature,
} from '../graphql/signature'
import { submitQueueable } from '../helpers/submitQueueable'
import { useNetwork } from '../hooks/useNetwork'
import { useToast } from '../hooks/useToast'

interface FormValues {
  personId?: number
  image: string
}

interface CustomerSignatureModalProps {
  orderId: number
  onDismiss?: () => void
}

export const CustomerSignatureModal = ({
  orderId,
  onDismiss,
}: CustomerSignatureModalProps) => {
  const { _ } = useLingui()
  const { isOnline } = useNetwork()
  const [presentToast] = useToast()
  const [completed, setCompleted] = useState(false)

  const form = useForm<FormValues>()
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = form

  const personId = useWatch({ control, name: 'personId' })

  // Fetch order data
  const [orderResult] = useQuery({
    query: GetOrderDataForSignature,
    variables: { orderId },
  })
  const order = orderResult.data?.order
  const orderContacts = order?.contacts || []
  const customerId = order?.customer?.id

  const title = _(msg`Terms & Conditions`)

  // Fetch customer contacts
  const [customerContactsResult] = useQuery({
    query: GetCustomerContacts,
    variables: { customerId: customerId || -1 },
    pause: customerId === undefined,
  })
  const customerContacts =
    customerContactsResult.data?.customers?.[0]?.contacts || []

  // Mutation
  const [, collectSignature] = useMutation(CollectOrderSignature)

  // Build merged contacts list
  const contacts = useMemo(() => {
    const orderContactsSorted = orderContacts
      .slice()
      .sort((a, b) => {
        if (a.role?.slug === 'field') return -1
        if (b.role?.slug === 'field') return 1
        if (a.default) return -1
        if (b.default) return 1
        return (a.role?.sort || 0) - (b.role?.sort || 0)
      })
      .map(({ contact }) => contact)

    const contactIds = new Set(orderContactsSorted.map((c) => c.id))

    const additionalContacts = customerContacts.filter(
      (contact) => !contactIds.has(contact.id),
    )

    return [...orderContactsSorted, ...additionalContacts]
  }, [orderContacts, customerContacts])

  // Default to first contact
  useEffect(() => {
    if (contacts.length > 0) {
      setValue('personId', contacts[0].id)
    }
  }, [contacts, setValue])

  const onFormSubmit = async (data: FormValues) => {
    // Contacts exist but none is selected — the default-select effect lost a
    // race; block to avoid persisting an unattributed signature.
    if (contacts.length > 0 && data.personId == null) {
      presentToast({
        color: 'danger',
        message: _(msg`Please select a name`),
        duration: 3000,
      })
      return
    }
    const exec = collectSignature({ data, where: { id: orderId } })

    // Never block the user on the network: the signature is optimistic + queued.
    // Offline or a too-slow connection → confirm and let it sync in the
    // background; a responsive connection still surfaces real server errors.
    const outcome = await submitQueueable(exec, { isOnline })
    if (outcome.status === 'queued') {
      presentToast({
        message: _(msg`Saved. It will sync when you're back online.`),
        duration: 3000,
      })
      setCompleted(true)
      return
    }
    if (outcome.status === 'error') {
      const errMsg = outcome.result.error?.message ?? _(msg`An error occurred`)
      presentToast({
        color: 'danger',
        message: `${_(msg`Error`)}: ${errMsg}`,
        duration: 4000,
      })
      return
    }
    setCompleted(true)
  }

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{title}</IonTitle>
          <IonButtons slot='end'>
            <IonButton onClick={onDismiss}>{_(msg`Close`)}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {!completed && (
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <IonList>
              <IonItem>
                <IonLabel position='fixed'>{_(msg`Name`)}</IonLabel>
                <IonSelect
                  interface='action-sheet'
                  value={personId}
                  onIonChange={(e) => setValue('personId', e.detail.value)}
                >
                  {contacts.map((contact) => (
                    <IonSelectOption key={contact.id} value={contact.id}>
                      {contact.firstName} {contact.lastName}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonItem>
                <div style={{ minWidth: '100%' }}>
                  {errors.image && (
                    <Text
                      variant='body'
                      className='text-danger'
                      style={{ fontSize: '0.8em', fontWeight: 700 }}
                    >
                      {errors.image.message}
                    </Text>
                  )}
                  <Signature
                    name='image'
                    control={control as unknown as Control<FieldValues>}
                    rules={{
                      required: _(msg`Please sign in the box below`),
                    }}
                    style={{ width: '100%' }}
                  />
                </div>
              </IonItem>
            </IonList>

            <div className='ion-text-center ion-padding'>
              <TermsAcknowledgment
                content={order?.terms?.content}
                textStyle={{
                  fontSize: '13px',
                  lineHeight: '17px',
                  color: 'var(--ion-color-step-700, #4d4d4d)',
                }}
              />
              <IonButton
                type='submit'
                style={{ marginTop: '10px', minWidth: '70%' }}
              >
                {_(msg`Submit`)}
              </IonButton>
            </div>
          </form>
        )}
        {completed && (
          <button
            type='button'
            className='ion-padding ion-text-center'
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              margin: 0,
              font: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
              textAlign: 'center' as const,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
            }}
            onClick={onDismiss}
          >
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                background:
                  'linear-gradient(135deg, var(--cs-color-success-bg, #e8f5e9), var(--cs-color-success-bg, #c8e6c9))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                boxShadow: '0 4px 16px rgba(76,175,80,0.12)',
              }}
            >
              <IonIcon
                icon={checkmarkCircleOutline}
                style={{
                  fontSize: '40px',
                  color: 'var(--cs-color-success, #4caf50)',
                }}
              />
            </div>
            <div
              style={{
                fontSize: '22px',
                fontWeight: 700,
                color: 'var(--cs-color-text, #1a1a1a)',
                marginBottom: '8px',
              }}
            >
              {_(msg`Signature Accepted`)}
            </div>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--cs-color-text-secondary, #999)',
                lineHeight: '1.4',
              }}
            >
              {_(msg`Please return this device to the Operator.`)}
            </div>
          </button>
        )}
      </IonContent>
    </>
  )
}

export default CustomerSignatureModal
